// SpeedInscription.jsx — Page publique d'inscription à une session Speed Business.
import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Loader2, CheckCircle2, Calendar, MapPin, PartyPopper, AlertCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SpeedInscription() {
  const { token } = useParams();
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ prenom: '', nom: '', entreprise: '', metier: '', email: '', telephone: '', type: 'invite' });

  useEffect(() => {
    axios.get(`${API}/speed-public/${token}`)
      .then(({ data }) => setSession(data))
      .catch((e) => setError(e?.response?.data?.detail || 'Lien invalide'))
      .finally(() => setLoading(false));
  }, [token]);

  const accent = session?.asso?.couleur_principale || '#0047FF';

  const submit = async (e) => {
    e.preventDefault();
    if (!form.prenom.trim() || !form.nom.trim() || !form.email.trim()) {
      setError('Prénom, nom et email sont obligatoires.');
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      await axios.post(`${API}/speed-public/${token}/inscription`, form);
      setDone(true);
    } catch (e2) {
      setError(e2?.response?.data?.detail || "Échec de l'inscription");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="min-h-screen grid place-items-center bg-[#F8FAFC]"><Loader2 className="w-7 h-7 animate-spin text-[#94A3B8]" /></div>;
  }

  if (error && !session) {
    return (
      <div className="min-h-screen grid place-items-center bg-[#F8FAFC] p-6">
        <div className="bg-white rounded-3xl shadow-lg p-8 max-w-md text-center">
          <AlertCircle className="w-10 h-10 mx-auto mb-3 text-[#DC2626]" />
          <h1 className="font-display text-xl font-bold text-[#0F172A] mb-1">Lien indisponible</h1>
          <p className="text-sm text-[#475569]">{error}</p>
        </div>
      </div>
    );
  }

  if (done) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] p-4 sm:p-8 flex items-start justify-center">
        <div className="bg-white rounded-3xl shadow-lg p-8 max-w-md w-full text-center mt-8" data-testid="speed-inscription-success">
          <div className="w-16 h-16 rounded-2xl grid place-items-center mx-auto mb-5" style={{ background: `${accent}1A`, color: accent }}>
            <PartyPopper className="w-8 h-8" />
          </div>
          <h1 className="font-display text-2xl font-black text-[#0F172A] mb-2">Inscription confirmée&nbsp;!</h1>
          <p className="text-sm text-[#475569] leading-relaxed">
            Merci {form.prenom}, votre inscription à <strong>{session.nom}</strong> est enregistrée.
            Nous avons hâte de vous rencontrer&nbsp;!
          </p>
          <div className="mt-6 text-sm text-[#334155] bg-[#F8FAFC] rounded-2xl p-4 text-left space-y-2">
            {session.date_session && <div className="flex items-center gap-2"><Calendar className="w-4 h-4" style={{ color: accent }} /> {session.date_session}</div>}
            {session.lieu && <div className="flex items-center gap-2"><MapPin className="w-4 h-4" style={{ color: accent }} /> {session.lieu}</div>}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC] p-4 sm:p-8 flex items-start justify-center">
      <div className="max-w-lg w-full mt-4">
        <HeaderBrand session={session} accent={accent} />
        <div className="rounded-3xl overflow-hidden shadow-lg">
          <div className="p-7 text-white" style={{ background: accent }}>
            <div className="text-[11px] font-bold uppercase tracking-[0.18em] opacity-80 mb-2">Speed Business</div>
            <h1 className="font-display text-2xl sm:text-3xl font-black leading-tight" data-testid="speed-inscription-title">{session.nom}</h1>
            {(session.date_session || session.lieu) && (
              <div className="mt-4 space-y-1.5 text-sm opacity-95">
                {session.date_session && <div className="flex items-center gap-2"><Calendar className="w-4 h-4" /> {session.date_session}</div>}
                {session.lieu && <div className="flex items-center gap-2"><MapPin className="w-4 h-4" /> {session.lieu}</div>}
              </div>
            )}
            {session.description && <p className="mt-4 text-sm opacity-90 leading-relaxed">{session.description}</p>}
          </div>

          <div className="bg-white p-7">
            {!session.inscription_ouverte ? (
              <div className="text-center py-6" data-testid="speed-inscription-closed">
                <AlertCircle className="w-8 h-8 mx-auto mb-2 text-[#94A3B8]" />
                <p className="text-sm text-[#475569]">Les inscriptions pour cet événement sont actuellement fermées.</p>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-3.5" data-testid="speed-inscription-form">
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Prénom *" testid="si-prenom" value={form.prenom} onChange={(v) => setForm({ ...form, prenom: v })} />
                  <Field label="Nom *" testid="si-nom" value={form.nom} onChange={(v) => setForm({ ...form, nom: v })} />
                </div>
                <Field label="Entreprise" testid="si-entreprise" value={form.entreprise} onChange={(v) => setForm({ ...form, entreprise: v })} />
                <Field label="Métier" testid="si-metier" value={form.metier} onChange={(v) => setForm({ ...form, metier: v })} />
                <Field label="Email *" type="email" testid="si-email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} />
                <Field label="Téléphone" type="tel" testid="si-telephone" value={form.telephone} onChange={(v) => setForm({ ...form, telephone: v })} />

                <div>
                  <label className="block text-[10px] font-bold uppercase tracking-[0.14em] text-[#475569] mb-2">Vous êtes</label>
                  <div className="grid grid-cols-2 gap-2">
                    {[{ v: 'adherent', l: 'Adhérent' }, { v: 'invite', l: 'Invité' }].map((o) => (
                      <button
                        type="button"
                        key={o.v}
                        onClick={() => setForm({ ...form, type: o.v })}
                        className="py-2.5 rounded-xl border-2 text-sm font-bold transition-colors"
                        style={form.type === o.v ? { borderColor: accent, color: accent, background: `${accent}0D` } : { borderColor: '#E2E8F0', color: '#475569' }}
                        data-testid={`si-type-${o.v}`}
                      >
                        {o.l}
                      </button>
                    ))}
                  </div>
                </div>

                {error && <div className="text-sm text-[#DC2626] bg-[#FEF2F2] rounded-xl px-3 py-2" data-testid="speed-inscription-error">{error}</div>}

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full inline-flex items-center justify-center gap-2 py-3.5 rounded-xl text-white font-bold text-base shadow-md disabled:opacity-60"
                  style={{ background: accent }}
                  data-testid="speed-inscription-submit"
                >
                  {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <CheckCircle2 className="w-5 h-5" />}
                  Je m&apos;inscris
                </button>
                <p className="text-[11px] text-[#94A3B8] text-center">En vous inscrivant, vous acceptez d&apos;être recontacté par {session.asso?.nom}.</p>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function HeaderBrand({ session, accent }) {
  return (
    <div className="flex items-center gap-3 mb-6">
      {session?.asso?.logo_url
        ? <img src={session.asso.logo_url} alt={session.asso.nom} className="h-10 w-auto" />
        : <div className="w-10 h-10 rounded-xl grid place-items-center text-white font-display font-black" style={{ background: accent }}>{(session?.asso?.nom || 'A')[0]}</div>}
      <span className="font-display font-black text-[#0F172A]">{session?.asso?.nom}</span>
    </div>
  );
}

function Field({ label, value, onChange, type = 'text', testid }) {
  return (
    <div>
      <label className="block text-[10px] font-bold uppercase tracking-[0.14em] text-[#475569] mb-1.5">{label}</label>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} className="ap-input" data-testid={testid} />
    </div>
  );
}
